/**
 * Workshop booking — app-driven config (poll interval, gates).
 * Bootstrap: #WcfConfig-{sectionId} from shop metafield amc.workshop_booking_config.
 * Runtime: client_config + config_version on public API responses.
 */
(function (global) {
  'use strict';

  var DEFAULT_POLL_MS = 30000;
  var DEFAULT_BOOKING_POLL_MS = 5000;
  var MIN_POLL_MS = 5000;
  var MAX_POLL_MS = 120000;
  var PROD_API_BASE = 'https://adapter.aogen.com.au';
  var CONFIG_PATH = '/api/public/workshop-booking/config';
  var VALIDATE_PATH = '/api/public/workshop-venue/capacity/validate';

  /* Soft verify mode: "we could not verify" / stale-data paths stop blocking.
     Real verdicts (sold out, hidden, ended, cancelled, over capacity) still block.
     Set window.__WB_CAPACITY_VERIFY_SOFT__ = false to restore hard blocking. */
  var VERIFY_SOFT_MODE = true;

  function verifySoftMode() {
    if (typeof global.__WB_CAPACITY_VERIFY_SOFT__ === 'boolean') {
      return global.__WB_CAPACITY_VERIFY_SOFT__;
    }
    return VERIFY_SOFT_MODE;
  }

  function resolveAogenApiBase() {
    var raw =
      (typeof global.__AOGEN_API_BASE__ === 'string' && global.__AOGEN_API_BASE__.trim()) ||
      (typeof global.__WB_FORMS_API_BASE__ === 'string' && global.__WB_FORMS_API_BASE__.trim()) ||
      '';
    raw = String(raw || '').replace(/\/+$/, '');
    return raw || PROD_API_BASE;
  }

  function defaultConfig() {
    return {
      version: 1,
      live_state: {
        poll: {
          enabled: true,
          interval_ms: DEFAULT_POLL_MS,
          interval_browsing_ms: DEFAULT_POLL_MS,
          interval_booking_ms: DEFAULT_BOOKING_POLL_MS,
          interval_cart_open_ms: 10000,
          min_ms: MIN_POLL_MS,
          max_ms: MAX_POLL_MS,
          on_visibility: true,
        },
      },
      enforce: {
        source: 'live_state_only',
        use_display_status: true,
        subtract_cart_seats: true,
        max_qty_per_order: 20,
      },
      gates: {
        live_fetch_before_add: true,
        live_fetch_on_cart_open: true,
        live_fetch_on_qty_change: true,
        max_stale_ms_before_add: 15000,
        max_stale_ms_before_qty_change: 15000,
        validate_before_add: true,
        validate_before_cart_update: false,
        validate_before_checkout: true,
      },
      policy: {
        validate_fail_mode: 'closed',
      },
      api: {
        validate_url: '',
      },
    };
  }

  function isPlainObject(v) {
    return v && typeof v === 'object' && !Array.isArray(v);
  }

  function deepMerge(base, partial) {
    if (!isPlainObject(partial)) return base || {};
    var out = isPlainObject(base) ? Object.assign({}, base) : {};
    Object.keys(partial).forEach(function (key) {
      var val = partial[key];
      if (isPlainObject(val)) {
        out[key] = deepMerge(out[key], val);
      } else if (val !== undefined) {
        out[key] = val;
      }
    });
    return out;
  }

  function readJsonScript(id) {
    var el = global.document && global.document.getElementById(id);
    if (!el || !el.textContent) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (_) {
      return null;
    }
  }

  function sectionId(root) {
    return (root && root.getAttribute('data-section-id')) || '';
  }

  function surfaceForRoot(root) {
    if (root && root.hasAttribute('data-workshop-city-wizard')) return 'workshop_city_flow';
    if (root && root.hasAttribute('data-workshop-booking-wizard')) return 'workshop_booking_wizard';
    if (root && root.hasAttribute('data-state-workshop-collection')) return 'state_workshop_collection';
    return 'workshop_booking';
  }

  function pollContextForRoot(root) {
    if (!root) return null;
    if (typeof root.__wcfGetPollContext === 'function') return root.__wcfGetPollContext();
    if (typeof root.__wbwGetPollContext === 'function') return root.__wbwGetPollContext();
    if (root.hasAttribute('data-state-workshop-collection')) return 'browsing';
    return null;
  }

  function ensureRootState(root) {
    if (!root) return defaultConfig();
    if (!root.__wcfBookingConfig) {
      var sid = sectionId(root);
      var bootstrap = readJsonScript('WcfConfig-' + sid) || {};
      root.__wcfBookingConfig = deepMerge(defaultConfig(), bootstrap);
    }
    return root.__wcfBookingConfig;
  }

  function resolveConfigApiUrl() {
    return resolveAogenApiBase() + CONFIG_PATH;
  }

  function resolveValidateApiUrl() {
    return resolveAogenApiBase() + VALIDATE_PATH;
  }

  function validateGateEnabled(root, intent) {
    var gates = ensureRootState(root).gates || {};
    if (intent === 'update_line') return gates.validate_before_cart_update === true;
    if (intent === 'checkout') return gates.validate_before_checkout !== false;
    if (intent === 'add_to_cart') return gates.validate_before_add !== false;
    return false;
  }

  function policyFor(root) {
    return ensureRootState(root).policy || {};
  }

  function validateFailModeOpen(root) {
    if (verifySoftMode()) return true;
    return String(policyFor(root).validate_fail_mode || 'closed').toLowerCase() === 'open';
  }

  function liveFetchFailModeOpen(root) {
    if (verifySoftMode()) return true;
    return String(policyFor(root).live_fetch_fail_mode || 'closed').toLowerCase() === 'open';
  }

  function extractMeta(body) {
    if (!body || typeof body !== 'object') {
      return { config_version: null, client_config: null };
    }
    return {
      config_version: body.config_version != null ? body.config_version : null,
      client_config: isPlainObject(body.client_config) ? body.client_config : null,
    };
  }

  function livePoll(root) {
    var cfg = ensureRootState(root);
    return (cfg.live_state && cfg.live_state.poll) || {};
  }

  function pollEnabled(root) {
    var poll = livePoll(root);
    return poll.enabled !== false;
  }

  function clampPollMs(n, poll) {
    n = parseInt(n, 10);
    if (!Number.isFinite(n) || n <= 0) n = DEFAULT_POLL_MS;
    var min = poll.min_ms != null ? parseInt(poll.min_ms, 10) : MIN_POLL_MS;
    var max = poll.max_ms != null ? parseInt(poll.max_ms, 10) : MAX_POLL_MS;
    if (Number.isFinite(min) && n < min) n = min;
    if (Number.isFinite(max) && n > max) n = max;
    return n;
  }

  function mergeClientConfigSections(target, clientConfig) {
    if (!isPlainObject(clientConfig)) return target || defaultConfig();
    var out = isPlainObject(target) ? target : defaultConfig();
    if (isPlainObject(clientConfig.gates)) {
      out.gates = deepMerge(out.gates || {}, clientConfig.gates);
    }
    if (isPlainObject(clientConfig.enforce)) {
      out.enforce = deepMerge(out.enforce || {}, clientConfig.enforce);
    }
    if (isPlainObject(clientConfig.policy)) {
      out.policy = deepMerge(out.policy || {}, clientConfig.policy);
    }
    if (isPlainObject(clientConfig.live_state)) {
      out.live_state = deepMerge(out.live_state || {}, clientConfig.live_state);
    }
    if (isPlainObject(clientConfig.api)) {
      out.api = deepMerge(out.api || {}, clientConfig.api);
    }
    return out;
  }

  function persistRootConfigScript(root) {
    if (!root || !root.__wcfBookingConfig) return;
    var el = global.document && global.document.getElementById('WcfConfig-' + sectionId(root));
    if (!el) return;
    try {
      el.textContent = JSON.stringify(root.__wcfBookingConfig);
    } catch (_) {}
  }

  function pollIntervalMs(root, context) {
    var poll = livePoll(root);
    var ms =
      poll.interval_ms != null && poll.interval_ms !== ''
        ? poll.interval_ms
        : DEFAULT_POLL_MS;
    if (context === 'browsing' && poll.interval_browsing_ms != null) {
      ms = poll.interval_browsing_ms;
    } else if (context === 'booking' && poll.interval_booking_ms != null) {
      ms = poll.interval_booking_ms;
    }
    return clampPollMs(ms, poll);
  }

  function cartOpenPollIntervalMs(root) {
    var poll = livePoll(root);
    var ms =
      poll.interval_cart_open_ms != null && poll.interval_cart_open_ms !== ''
        ? poll.interval_cart_open_ms
        : 10000;
    return clampPollMs(ms, poll);
  }

  function activePollIntervalMs(root) {
    return pollIntervalMs(root, pollContextForRoot(root));
  }

  function livePollController(root) {
    if (!root) return null;
    return root.__wcfLivePoll || root.__swcLivePoll || root.__wbwLivePoll || null;
  }

  function triggerLiveRefresh(root) {
    var pollCtrl = livePollController(root);
    if (pollCtrl && typeof pollCtrl.refresh === 'function') {
      pollCtrl.refresh();
    }
  }

  function syncPollContext(root) {
    if (!root) return false;
    var pollCtrl = livePollController(root);
    if (pollCtrl && typeof pollCtrl.syncInterval === 'function') {
      return pollCtrl.syncInterval();
    }
    return maybeRestartPoll(root, 'context');
  }

  function maybeRestartPoll(root, reason) {
    if (!root) return false;
    var ms = activePollIntervalMs(root);
    if (root.__wcfLastPollIntervalMs === ms) return false;
    root.__wcfLastPollIntervalMs = ms;
    if (typeof root.__wcfRestartLivePoll === 'function') {
      root.__wcfRestartLivePoll(reason || 'interval');
      return true;
    }
    if (typeof root.__swcRestartLivePoll === 'function') {
      root.__swcRestartLivePoll(reason || 'interval');
      return true;
    }
    if (typeof root.__wbwRestartLivePoll === 'function') {
      root.__wbwRestartLivePoll(reason || 'interval');
      return true;
    }
    return false;
  }

  function maybeRefreshFullConfig(root) {
    var url = resolveConfigApiUrl(root);
    if (!url) {
      return Promise.resolve(false);
    }
    return fetch(url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        shop_domain: (global.Shopify && global.Shopify.shop) || '',
        intent: 'load_workshop_booking_config',
        surface: surfaceForRoot(root),
      }),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (body) {
        var next = body && (body.config || body);
        if (!next || typeof next !== 'object') return false;
        root.__wcfBookingConfig = deepMerge(defaultConfig(), next);
        var el = global.document.getElementById('WcfConfig-' + sectionId(root));
        if (el) {
          try {
            el.textContent = JSON.stringify(root.__wcfBookingConfig);
          } catch (_) {}
        }
        return true;
      })
      .catch(function (err) {
        console.warn('[workshop-booking-config] config refresh failed', err);
        return false;
      });
  }

  function applyMetaFromResponse(root, body) {
    if (!root) return Promise.resolve({ changed: false, versionChanged: false, pollChanged: false });

    ensureRootState(root);
    var meta = extractMeta(body);
    var versionChanged = false;
    var pollChanged = false;
    var prevBrowse = pollIntervalMs(root, 'browsing');
    var prevBook = pollIntervalMs(root, 'booking');
    var prevCartOpen = cartOpenPollIntervalMs(root);

    if (meta.client_config) {
      root.__wcfBookingConfig = mergeClientConfigSections(root.__wcfBookingConfig, meta.client_config);
      persistRootConfigScript(root);
      pollChanged =
        pollIntervalMs(root, 'browsing') !== prevBrowse ||
        pollIntervalMs(root, 'booking') !== prevBook ||
        cartOpenPollIntervalMs(root) !== prevCartOpen;
    }

    if (meta.config_version != null) {
      var localV = Number(root.__wcfBookingConfig.version) || 1;
      var apiV = Number(meta.config_version);
      if (Number.isFinite(apiV) && apiV !== localV) {
        versionChanged = true;
      }
    }

    function finish(refreshed) {
      var restarted = false;
      var pollChangedLocal = pollChanged;
      try {
        restarted = maybeRestartPoll(root, versionChanged ? 'config_version' : 'client_config');
        if (versionChanged || pollChangedLocal) {
          triggerLiveRefresh(root);
        }
        if (
          pollChangedLocal &&
          global.WorkshopCityCartLive &&
          typeof global.WorkshopCityCartLive.restartDrawerPoll === 'function'
        ) {
          global.WorkshopCityCartLive.restartDrawerPoll();
        }
      } catch (_) {}
      return {
        changed: !!(refreshed || pollChangedLocal || versionChanged || restarted),
        versionChanged: versionChanged,
        pollChanged: pollChangedLocal || restarted,
      };
    }

    if (versionChanged) {
      return maybeRefreshFullConfig(root).then(function (refreshed) {
        return finish(refreshed);
      });
    }

    return Promise.resolve(finish(false));
  }

  function applyClientConfigToCartRoots(clientConfig) {
    if (!clientConfig) return;
    ['cart-drawer', 'cart-page'].forEach(function (surface) {
      var root = {
        getAttribute: function (name) {
          if (name !== 'data-section-id') return '';
          return surface;
        },
      };
      ensureRootState(root);
      root.__wcfBookingConfig = mergeClientConfigSections(root.__wcfBookingConfig, clientConfig);
      persistRootConfigScript(root);
    });
    if (global.WorkshopCityCartLive && typeof global.WorkshopCityCartLive.restartDrawerPoll === 'function') {
      global.WorkshopCityCartLive.restartDrawerPoll();
    }
  }

  var api = {
    DEFAULT_POLL_MS: DEFAULT_POLL_MS,
    DEFAULT_BOOKING_POLL_MS: DEFAULT_BOOKING_POLL_MS,
    defaultConfig: defaultConfig,
    readBootstrap: ensureRootState,
    mergeClientConfig: deepMerge,
    mergeClientConfigSections: mergeClientConfigSections,
    applyClientConfigToCartRoots: applyClientConfigToCartRoots,
    applyMetaFromResponse: applyMetaFromResponse,
    pollIntervalMs: pollIntervalMs,
    cartOpenPollIntervalMs: cartOpenPollIntervalMs,
    activePollIntervalMs: activePollIntervalMs,
    pollContextForRoot: pollContextForRoot,
    pollEnabled: pollEnabled,
    syncPollContext: syncPollContext,
    maybeRefreshFullConfig: maybeRefreshFullConfig,
    resolveConfigApiUrl: resolveConfigApiUrl,
    resolveValidateApiUrl: resolveValidateApiUrl,
    validateGateEnabled: validateGateEnabled,
    verifySoftMode: verifySoftMode,
    validateFailModeOpen: validateFailModeOpen,
    liveFetchFailModeOpen: liveFetchFailModeOpen,
    extractMeta: extractMeta,
    triggerLiveRefresh: triggerLiveRefresh,
  };

  global.WorkshopBookingConfig = api;
  global.WorkshopCityConfig = api;
})(typeof window !== 'undefined' ? window : this);
